import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  ClipboardCheck, 
  Eye, 
  CheckCircle, 
  Clock, 
  User,
  Calendar,
  DollarSign,
  ArrowRight,
  AlertCircle,
  FileText
} from 'lucide-react';

const EstimateReviewQueue = () => {
  const { organization, user } = useAuth();
  const navigate = useNavigate();
  const [pendingEstimates, setPendingEstimates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);

  useEffect(() => {
    if (organization?.id) {
      loadPendingEstimates();
    }
  }, [organization?.id]);

  const loadPendingEstimates = async () => {
    try {
      const response = await apiHelpers.getEstimatesPendingReview(organization.id);
      setPendingEstimates(response.data.data || []);
    } catch (error) {
      console.error('Failed to load pending estimates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (estimateId) => {
    setApprovingId(estimateId);
    try {
      await apiHelpers.approveEstimate(organization.id, estimateId, user?.id, user?.name);
      toast.success('Estimate approved!');
      setPendingEstimates(prev => prev.filter(e => e.id !== estimateId));
    } catch (error) {
      console.error('Failed to approve estimate:', error);
      toast.error('Failed to approve estimate');
    } finally {
      setApprovingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Don't render if user is staff (they can't approve)
  if (user?.role === 'staff') return null;

  // Don't render if no pending estimates
  if (!isLoading && pendingEstimates.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 md:p-6" data-testid="estimate-review-queue">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <ClipboardCheck className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-amber-900">Estimates Pending Review</h3>
            <p className="text-sm text-amber-700">
              {pendingEstimates.length} estimate{pendingEstimates.length !== 1 ? 's' : ''} from staff awaiting approval
            </p>
          </div>
        </div>
        {pendingEstimates.length > 0 && (
          <span className="px-3 py-1 bg-amber-200 text-amber-800 rounded-full text-sm font-medium">
            {pendingEstimates.length} pending
          </span>
        )}
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="text-center py-8 text-amber-700">
          <div className="w-6 h-6 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          Loading...
        </div>
      ) : (
        <div className="space-y-3">
          {pendingEstimates.map((estimate) => (
            <div 
              key={estimate.id}
              className="bg-white rounded-lg border border-amber-100 p-4 hover:border-amber-300 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                {/* Left: Estimate Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="font-medium text-slate-900 truncate">
                      {estimate.client_name || 'Unknown Client'}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                      {estimate.service_type || 'General'}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                    {estimate.created_by_name && (
                      <span className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        {estimate.created_by_name}
                      </span>
                    )}
                    {estimate.site_visit_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        Visit: {formatDate(estimate.site_visit_date)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5" />
                      {formatCurrency(estimate.total)}
                    </span>
                  </div>
                  
                  {estimate.site_visit_title && (
                    <p className="text-xs text-slate-500 mt-1 truncate">
                      From: {estimate.site_visit_title}
                    </p>
                  )}
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/estimates/${estimate.id}`)}
                    className="text-slate-600"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Review
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleApprove(estimate.id)}
                    disabled={approvingId === estimate.id}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {approvingId === estimate.id ? (
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View All Link */}
      {pendingEstimates.length > 3 && (
        <div className="mt-4 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/estimates?filter=pending_review')}
            className="text-amber-700 hover:text-amber-800"
          >
            View all pending estimates
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default EstimateReviewQueue;
